import {useEffect, useState} from 'react';
import {Button, Input, Select, Typography} from 'antd';
import DBModal from 'components/modal/modal';
import {documentDetailsChanges, needsBeneficialOwner} from './kycHubUtils';
import {BeneficialOwnerSelect} from './BeneficialOwnerSelect';
import styles from './kycHub.module.scss';

const {Text} = Typography;

const formFrom = document => ({
  documentType: document?.documentType,
  freeName: document?.freeName || '',
  fileName: document?.fileName || '',
  beneficialOwnerId: document?.beneficialOwnerId || undefined,
  reason: '',
});

/**
 * Reclassify or rename one active document without touching the file: its
 * type, its name (for `OTHER`), its file name and, for a beneficial owner's
 * ID, which owner it belongs to.
 *
 * Unlike replace and archive this is allowed in any KYB status — fixing a
 * mislabelled upload does not reopen the submission. Only the changed fields
 * are sent, with the required reason. `onSubmit` resolves true on success.
 */
export const EditDocumentDetailsModal = ({
  open,
  document,
  subjectType,
  documentTypes,
  beneficialOwners,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(formFrom(document));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(formFrom(document));
      setTouched(false);
    }
  }, [open, document]);

  const set = patch => setForm(current => ({...current, ...patch}));

  const {changes, errors} = documentDetailsChanges(document, form, subjectType);
  const invalid = Object.values(errors).some(Boolean);
  const ownerRequired = needsBeneficialOwner(subjectType, form.documentType);

  const handleSubmit = () => {
    setTouched(true);
    if (invalid) return;
    onSubmit({
      documentId: document.id,
      ...changes,
      reason: form.reason.trim(),
    });
  };

  const fieldError = key =>
    touched &&
    errors[key] && (
      <Text type="danger" className={styles.fieldError}>
        {errors[key]}
      </Text>
    );

  return (
    <DBModal
      isOpen={open}
      handleClose={submitting ? undefined : onClose}
      width={520}
      destroyOnClose
      content={
        <div className={styles.modalBody}>
          <h2 className={styles.modalTitle}>Edit document details</h2>
          <Text type="secondary">
            {document?.freeName || document?.fileName || 'Uploaded file'}. The
            file itself and its review status stay as they are.
          </Text>

          <div className={styles.modalField}>
            <label className={styles.fieldLabel} htmlFor="kyc-hub-edit-type">
              Document type
            </label>
            <Select
              id="kyc-hub-edit-type"
              className={styles.fullWidth}
              placeholder="Choose a type"
              value={form.documentType}
              onChange={documentType => set({documentType})}
              optionLabelProp="label">
              {documentTypes.map(type => (
                <Select.Option
                  key={type.code}
                  value={type.code}
                  label={type.en}>
                  <div className={styles.cell}>
                    <span>{type.en}</span>
                    <span className={styles.subtle}>French: {type.fr}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
            {fieldError('documentType')}
          </div>

          {form.documentType === 'OTHER' && (
            <div className={styles.modalField}>
              <label className={styles.fieldLabel} htmlFor="kyc-hub-edit-name">
                Document name
              </label>
              <Input
                id="kyc-hub-edit-name"
                value={form.freeName}
                maxLength={120}
                placeholder="e.g. Inheritance declaration"
                onChange={event => set({freeName: event.target.value})}
              />
              {fieldError('freeName')}
            </div>
          )}

          {ownerRequired && (
            <div className={styles.modalField}>
              <label className={styles.fieldLabel} htmlFor="kyc-hub-edit-owner">
                Beneficial owner
              </label>
              <BeneficialOwnerSelect
                id="kyc-hub-edit-owner"
                owners={beneficialOwners}
                value={form.beneficialOwnerId}
                onChange={beneficialOwnerId => set({beneficialOwnerId})}
              />
              {fieldError('beneficialOwnerId')}
            </div>
          )}

          <div className={styles.modalField}>
            <label className={styles.fieldLabel} htmlFor="kyc-hub-edit-file">
              File name
            </label>
            <Input
              id="kyc-hub-edit-file"
              value={form.fileName}
              maxLength={200}
              onChange={event => set({fileName: event.target.value})}
            />
            {fieldError('fileName')}
          </div>

          <div className={styles.modalField}>
            <label className={styles.fieldLabel} htmlFor="kyc-hub-edit-reason">
              Reason (required)
            </label>
            <Input.TextArea
              id="kyc-hub-edit-reason"
              rows={3}
              value={form.reason}
              maxLength={1000}
              showCount
              placeholder="e.g. Uploaded under the wrong type"
              onChange={event => set({reason: event.target.value})}
            />
            <Text type="secondary" className={styles.fieldHint}>
              Recorded in the activity timeline with the change.
            </Text>
            {fieldError('reason')}
          </div>

          {touched && errors.changes && (
            <Text type="danger" className={styles.fieldError}>
              {errors.changes}
            </Text>
          )}

          <div className={styles.modalActions}>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>
              Save details
            </Button>
          </div>
        </div>
      }
    />
  );
};
